<template>
  <div class="step-template" v-loading="loading">
    <WelcomeBanner title="步骤模板管理" :subtitle="`共 ${pagination.total} 个模板`">
      <template #right>
        <el-button type="primary" @click="showCreateDialog">
          <i class="el-icon-plus"></i> 创建模板
        </el-button>
      </template>
    </WelcomeBanner>

    <!-- 统计卡片 -->
    <div class="stats-cards" v-if="!loading">
      <StatCard :value="stats.global" label="全局模板" icon="el-icon-collection" :gradient="'var(--stat-gradient-1)'" />
      <StatCard :value="stats.project" label="项目模板" icon="el-icon-folder-opened" :gradient="'var(--stat-gradient-2)'" />
      <StatCard :value="stats.active" label="启用中" icon="el-icon-circle-check" :gradient="'var(--stat-gradient-3)'" :active="true" />
      <StatCard :value="stats.inactive" label="已禁用" icon="el-icon-circle-close" :gradient="'var(--stat-gradient-4)'" :inactive="true" />
    </div>

    <!-- 筛选区 -->
    <div class="filter-section">
      <div class="filter-row">
        <el-input
          v-model="filters.keyword"
          placeholder="搜索模板名称"
          clearable
          style="width: 220px;"
          @clear="handleFilter"
          @keyup.enter.native="handleFilter"
        >
          <i slot="prefix" class="el-icon-search"></i>
        </el-input>
        <el-select v-model="filters.templateType" placeholder="模板类型" clearable style="width: 150px;" @change="handleFilter">
          <el-option label="全部" value="" />
          <el-option label="全局模板" value="global" />
          <el-option label="项目模板" value="project" />
        </el-select>
        <el-select v-model="filters.status" placeholder="状态" clearable style="width: 120px;" @change="handleFilter">
          <el-option label="全部" value="" />
          <el-option label="启用" :value="1" />
          <el-option label="禁用" :value="0" />
        </el-select>
        <el-button @click="handleReset">重置</el-button>
      </div>
    </div>

    <!-- 模板列表 -->
    <div class="template-container">
      <el-table :data="templateList" stripe style="width: 100%" :header-cell-style="{ background: '#f5f7fa', color: '#606266' }">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="template_id" label="ID" width="220" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <code class="template-id">{{ row.template_id }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="template_name" label="模板名称" min-width="150">
          <template slot-scope="{ row }">
            <el-link type="primary" @click="showDetail(row)">{{ row.template_name }}</el-link>
          </template>
        </el-table-column>
        <el-table-column prop="template_desc" label="描述" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.template_desc || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="类型" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.project_id ? 'warning' : 'success'" size="small" effect="plain">
              <i :class="row.project_id ? 'el-icon-folder-opened' : 'el-icon-collection'"></i>
              {{ row.project_id ? '项目' : '全局' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small" effect="plain">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="create_time" label="创建时间" width="160" align="center">
          <template slot-scope="{ row }">
            {{ formatTime(row.create_time) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" class="action-btn" @click="showDetail(row)">
              <i class="el-icon-view"></i> 详情
            </el-button>
            <el-button type="text" size="small" class="action-btn edit" @click="showEditDialog(row)">
              <i class="el-icon-edit"></i> 编辑
            </el-button>
            <el-dropdown trigger="click" @command="(cmd) => handleCommand(cmd, row)">
              <el-button type="text" size="small" class="action-btn more">
                <i class="el-icon-more"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="delete" icon="el-icon-delete" class="danger-item">
                  删除
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <el-empty v-if="!loading && templateList.length === 0" description="暂无模板数据" class="empty-state">
        <el-button type="primary" @click="showCreateDialog">创建第一个模板</el-button>
      </el-empty>

      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="pagination.total > 0">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :current-page="pagination.currentPage"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form ref="templateForm" :model="templateForm" :rules="templateRules" label-width="100px">
        <el-form-item label="模板名称" prop="template_name">
          <el-input v-model="templateForm.template_name" placeholder="请输入模板名称" />
        </el-form-item>
        <el-form-item label="描述" prop="template_desc">
          <el-input v-model="templateForm.template_desc" type="textarea" :rows="3" placeholder="请输入模板描述" />
        </el-form-item>
        <el-form-item label="模板类型" prop="isGlobal">
          <el-radio-group v-model="templateForm.isGlobal">
            <el-radio :label="true">全局模板</el-radio>
            <el-radio :label="false">项目模板</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="!templateForm.isGlobal" label="关联项目" prop="projectId">
          <el-select v-model="templateForm.projectId" placeholder="请选择关联项目" style="width: 100%;">
            <el-option
              v-for="project in projectList"
              :key="project.project_id"
              :label="project.project_name || project.name"
              :value="project.project_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch v-model="templateForm.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getTemplateList,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  changeTemplateStatus
} from '@/api/template'
import { getMyProjectList } from '@/api/project'
import StatCard from '@/components/StatCard.vue'
import WelcomeBanner from '@/components/WelcomeBanner.vue'

export default {
  name: 'StepTemplate',
  components: { StatCard, WelcomeBanner },
  data() {
    return {
      loading: false,
      templateList: [],
      projectList: [],
      filters: {
        keyword: '',
        templateType: '',
        status: null
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      stats: {
        global: 0,
        project: 0,
        active: 0,
        inactive: 0
      },
      dialogVisible: false,
      dialogTitle: '创建模板',
      isEdit: false,
      templateForm: {
        template_name: '',
        template_desc: '',
        isGlobal: true,
        projectId: null,
        status: 1
      },
      templateRules: {
        template_name: [{ required: true, message: '请输入模板名称', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.loadTemplates()
    this.loadProjects()
  },
  methods: {
    async loadTemplates() {
      this.loading = true
      try {
        let data
        if (this.filters.templateType === 'project') {
          this.templateList = []
          this.pagination.total = 0
          this.loading = false
          return
        } else {
          const params = {
            page: this.pagination.currentPage,
            page_size: this.pagination.pageSize,
            status: this.filters.status || 1
          }
          if (this.filters.keyword) {
            params.keyword = this.filters.keyword
          }
          data = await getTemplateList(params)
        }
        this.templateList = data?.list || []
        this.pagination.total = data?.total || 0
        this.updateStats()
      } catch (error) {
        console.error('加载模板列表失败:', error)
        this.$message.error('加载模板列表失败')
      } finally {
        this.loading = false
      }
    },

    updateStats() {
      const list = this.templateList
      this.stats.global = list.filter(t => !t.project_id).length
      this.stats.project = list.filter(t => t.project_id).length
      this.stats.active = list.filter(t => t.status === 1).length
      this.stats.inactive = list.filter(t => t.status === 0).length
    },

    async loadProjects() {
      try {
        const data = await getMyProjectList()
        this.projectList = data?.list || []
      } catch (error) {
        console.error('加载项目列表失败:', error)
      }
    },

    handleFilter() {
      this.pagination.currentPage = 1
      this.loadTemplates()
    },

    handleReset() {
      this.filters = { keyword: '', templateType: '', status: null }
      this.pagination.currentPage = 1
      this.loadTemplates()
    },

    handlePageChange(page) {
      this.pagination.currentPage = page
      this.loadTemplates()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.loadTemplates()
    },

    showCreateDialog() {
      this.isEdit = false
      this.dialogTitle = '创建模板'
      this.templateForm = {
        template_name: '',
        template_desc: '',
        isGlobal: true,
        projectId: null,
        status: 1
      }
      this.dialogVisible = true
    },

    showEditDialog(row) {
      this.isEdit = true
      this.dialogTitle = '编辑模板'
      this.templateForm = {
        templateId: row.template_id,
        template_name: row.template_name,
        template_desc: row.template_desc,
        isGlobal: !row.project_id,
        projectId: row.project_id,
        status: row.status
      }
      this.detailVisible = false
      this.dialogVisible = true
    },

    showDetail(row) {
      this.$router.push(`/template/${row.template_id}`)
    },

    handleDialogClose() {
      this.$refs.templateForm?.resetFields()
    },

    async handleSubmit() {
      try {
        await this.$refs.templateForm.validate()
        const data = {
          template_name: this.templateForm.template_name,
          template_desc: this.templateForm.template_desc,
          project_id: this.templateForm.isGlobal ? null : this.templateForm.projectId,
          status: this.templateForm.status
        }

        if (this.isEdit) {
          await updateTemplate(this.templateForm.templateId, data)
          this.$message.success('模板更新成功')
        } else {
          await createTemplate(data)
          this.$message.success('模板创建成功')
        }

        this.dialogVisible = false
        this.loadTemplates()
      } catch (error) {
        if (error !== false) {
          console.error('保存模板失败:', error)
          this.$message.error('保存失败')
        }
      }
    },

    async handleStatusChange(row) {
      try {
        const newStatus = row.status === 1 ? 0 : 1
        await this.$confirm(`确定要${newStatus === 1 ? '启用' : '禁用'}该模板吗？`, '提示', {
          type: 'warning'
        })
        await changeTemplateStatus(row.template_id, { status: newStatus })
        this.$message.success('状态更新成功')
        this.loadTemplates()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('更新状态失败:', error)
        }
      }
    },

    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该模板吗？此操作不可恢复。', '警告', {
          type: 'warning',
          confirmButtonText: '确定',
          cancelButtonText: '取消'
        })
        await deleteTemplate(row.template_id)
        this.$message.success('删除成功')
        this.loadTemplates()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除模板失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    handleCommand(command, row) {
      if (command === 'delete') {
        this.handleDelete(row)
      }
    },

    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    }
  }
}
</script>

<style lang="scss" scoped>
.step-template {
  padding: 20px;
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
  min-height: calc(100vh - 100px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(129, 140, 248, 0.25);

  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;

    h2 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #fff;
      display: inline;
    }

    .template-count {
      margin: 0 0 0 8px;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.95);
      padding: 6px 12px;
      background: rgba(255, 255, 255, 0.12);
      border-radius: 20px;
      backdrop-filter: blur(4px);
    }
  }

  .el-button {
    height: 36px;
    align-items: center;
    background: rgba(255,255,255,0.12);
    color: #fff;
    border: none;
  }

  .el-button:hover { background: rgba(255,255,255,0.18); }
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
  }

.filter-section {
  background: #fff;
  padding: 14px;
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

.filter-row { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }

.template-container {
  background: #fff;
  padding: 12px;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

.template-id {
  font-size: 11px;
  color: #909399;
  background: #f5f7fa;
  padding: 3px 8px;
  border-radius: 6px;
  font-family: 'Monaco','Consolas',monospace;
}

.action-btn { padding: 2px 6px; font-size:12px; }
.action-btn.edit { color: #6366f1; }
.action-btn.more { color: #909399; }

.empty-state { padding: 36px 0; text-align:center; color:#c0c4cc; }

.pagination-wrapper { display:flex; justify-content:center; margin-top:16px; }

.danger-text { color: #f56c6c !important; }

.template-detail { padding: 10px 0; }
.steps-section { margin-top:20px; }

@media (max-width: 1200px) {
  .stats-cards { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  .stats-cards { grid-template-columns: 1fr; }
  .page-header { flex-direction: column; align-items: flex-start; gap:12px; }
}
</style>
